import React from 'react';
import Color from 'color';
import styled from 'styled-components';
import { Box, FormGroup } from '@mui/material';
import { getColor } from 'spw-shared/utils';
import {
  INDIVIDUAL_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  SHARED_SPACES_CATEGORY,
  SHARED_SPACES_KEY,
} from 'spw-constants';
import { ShowHideDecimalToggle } from 'spw-components/global/ShowHideDecimalToggle';
import { JustifyStartRow } from 'spw-shared/globalComponents/grid';
import {
  IProject,
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { colors } from 'spw-theme';
import {
  SwitchFormControlLabel,
  SwitchLabel,
  Switch,
} from 'spw-components/global/styles';
import { Row } from 'spw-shared/globalComponents';
import { ColorDot, HeaderRow, WorksheetHeaderIcon } from './styles';
import { WorksheetLabel } from './worksheet-label';
import { PlainWorksheet } from '../../models/Worksheet';
import { SummaryCalculator } from '../../models/ProgramSummaryCalculator';
import { PlainProgramScenario } from '../../models/Scenario';

interface IProps {
  scenario?: PlainProgramScenario;
  worksheet?: PlainWorksheet;
  programSummaryCalculator: SummaryCalculator;
  projectColors: IProjectColors;
  project: IProject;
  showDecimals: boolean;
  toggleShowDecimals: () => void;
  scrollTo?: (cat: string) => void;
  shouldHideDecimalsSwitch?: boolean;
  clientFacingNamesAreVisible?: boolean;
  toggleClientFacingNamesVisibility?: () => void;
}

const Wrapper = styled.div`
  position: sticky;
  top: 0px;
  z-index: 2;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.containerBorder};
  display: grid;
  width: 100%;
  grid-template-columns: 292px 1fr;
  padding: 8px;
  box-sizing: border-box;

  .pill {
    display: flex;
    flex-direction: column;
    align-items: end;

    &:not(:last-child) {
      margin-bottom: 8px;
    }

    &.left-align {
      align-items: start;
    }

    .value {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: ${colors.black};
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 100%;
      cursor: default;

      &.limited {
        max-width: 140px;
      }

      &.positive {
        color: ${colors.mainGreen};
      }

      &.negative {
        color: ${colors.red};
      }
    }

    .label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: ${colors.fontColor};
      width: max-content;
      cursor: default;
    }
  }

  .left-column {
    min-width: 142px;
    padding: 8px 0 8px 8px;

    .MuiFormControlLabel-root {
      margin-left: -1px;

      .MuiSwitch-root {
        margin-left: 0;
      }

      .MuiFormControlLabel-label {
        margin-top: 2px;
      }
    }

    > *:nth-child(2) {
      margin-top: 4px;
    }
  }

  .right-column {
    display: flex;
    width: 100%;
  }
`;

const WorksheetHeader: React.FunctionComponent<IProps> = ({
  programSummaryCalculator,
  scenario,
  worksheet,
  projectColors,
  project,
  showDecimals,
  toggleShowDecimals,
  scrollTo,
  shouldHideDecimalsSwitch,
  clientFacingNamesAreVisible,
  toggleClientFacingNamesVisibility,
}) => {
  if (!scenario) return null;
  const { spaceTypeCategories } = project;

  const {
    circulationPercentage,
    totalProgramUSF,
    planningFactorPercentage,
    deltaProgramUSF,
  } = programSummaryCalculator || {};
  const { subtotalProgramUSF } = worksheet || {};

  const individualWorkspacesCategory = spaceTypeCategories.find(
    (spaceTypeCategory: IProjectSpaceTypeCategory) =>
      spaceTypeCategory.defaultSpaceTypeCategory.category ===
      INDIVIDUAL_WORKSPACES_CATEGORY
  );

  const individualDot = (
    <ColorDot
      color={getColor(INDIVIDUAL_WORKSPACES_CATEGORY, projectColors)}
      className="colorDot large-button"
      onClick={() => scrollTo(INDIVIDUAL_SPACES_CATEGORY)}
    >
      <WorksheetHeaderIcon
        src={`/images/${individualWorkspacesCategory?.icon}.svg`}
        alt={`${individualWorkspacesCategory?.name} icon`}
      />
    </ColorDot>
  );

  let sharedSpanColor = colors.mainGray as string;
  const luminosity = new Color(
    getColor(SHARED_SPACES_KEY, projectColors)
  ).luminosity();
  // if shared space color is dark, show white text
  if (luminosity < 0.5) {
    sharedSpanColor = colors.white;
  }

  return (
    <Wrapper data-cy="worksheet-header" className="top-header sticky">
      <div className="left-column">
        {scenario.isShared ? (
          <JustifyStartRow>
            {individualDot}

            <ColorDot
              color={getColor(SHARED_SPACES_KEY, projectColors)}
              className="add-margin large-button"
              onClick={() => scrollTo(SHARED_SPACES_CATEGORY)}
            >
              <div
                style={{
                  color: sharedSpanColor,
                  fontWeight: 400,
                  fontSize: '7px',
                }}
              >
                SHARED
              </div>
            </ColorDot>
          </JustifyStartRow>
        ) : (
          <JustifyStartRow>
            {individualDot}
            {spaceTypeCategories
              ?.filter(
                (spaceTypeCategory: IProjectSpaceTypeCategory) =>
                  spaceTypeCategory.defaultSpaceTypeCategory.category !==
                  INDIVIDUAL_WORKSPACES_CATEGORY
              )
              ?.map((spaceTypeCategory: IProjectSpaceTypeCategory) => {
                return (
                  <ColorDot
                    key={`dot-${spaceTypeCategory.id}`}
                    color={getColor(
                      spaceTypeCategory.defaultSpaceTypeCategory.category,
                      projectColors
                    )}
                    className="add-margin large-button"
                    onClick={() =>
                      scrollTo(
                        spaceTypeCategory.defaultSpaceTypeCategory.category
                      )
                    }
                  >
                    <WorksheetHeaderIcon
                      src={`/images/${spaceTypeCategory.icon}.svg`}
                      alt={`${spaceTypeCategory.name}-icon`}
                    />
                  </ColorDot>
                );
              })}
          </JustifyStartRow>
        )}
        <HeaderRow margin="10px 0px 0px 0px">
          <Row>
            {!shouldHideDecimalsSwitch && (
              <ShowHideDecimalToggle
                showDecimals={showDecimals}
                toggleShowDecimals={toggleShowDecimals}
                labelPlacement="start"
                testingId="worksheet_decimal_toggle"
              />
            )}
            {!shouldHideDecimalsSwitch && (
              <Box marginLeft={1.5}>
                <FormGroup aria-label="client facing names switch" row>
                  <SwitchFormControlLabel
                    control={
                      <Switch
                        checked={clientFacingNamesAreVisible}
                        onChange={toggleClientFacingNamesVisibility}
                        color="default"
                      />
                    }
                    label={<SwitchLabel>Client Names</SwitchLabel>}
                    labelPlacement="start"
                  />
                </FormGroup>
              </Box>
            )}
          </Row>
        </HeaderRow>
      </div>
      <div>
        <WorksheetLabel
          scenario={scenario}
          totalWorkSeats={scenario.workSeats}
          totalHeadcount={scenario.headcount}
          circulationPercentage={circulationPercentage}
          planningFactorPercentage={planningFactorPercentage}
          showDecimals={showDecimals}
          deltaProgramUSF={deltaProgramUSF}
          totalProgramUSF={totalProgramUSF}
          subtotalProgramUSF={subtotalProgramUSF}
          testingPrefix="worksheet_header"
        />
      </div>
    </Wrapper>
  );
};

const MemoizedWorksheetHeader = React.memo(WorksheetHeader);

MemoizedWorksheetHeader.whyDidYouRender = false;

export default MemoizedWorksheetHeader;
